// Insights (blog) content. Each post powers /insights/<slug> via BlogPost.jsx
// and is prerendered to static HTML (head + full article body) for crawlers
// and answer engines. Structure per post:
// answer, concise TL;DR optimised for featured snippets / AEO
// sections, h2 blocks with paragraphs, optional list or table
// faqs, rendered as accordion + FAQPage schema
// sources, outbound citations (E-E-A-T)
// related, internal links (services, jurisdictions, other posts)

export const AUTHOR = {
 name: "Aurelius Advisory Team",
 description:
 "Senior family-office practitioners advising HNW and UHNW families on structuring across DIFC, Switzerland, Singapore, London and the US.",
};

export const blogPosts = {
 "how-much-does-a-family-office-cost": {
 title: "How Much Does a Family Office Cost in 2026? The Full Breakdown",
 category: "Costs & Economics",
 metaTitle: "Family Office Cost in 2026: Setup & Annual Running Costs | Aurelius",
 metaDescription:
 "What a family office really costs in 2026: $1M-$10M annual running costs, 30-120 bps of AUM, setup fees by jurisdiction (DIFC, Switzerland, Singapore) and when an MFO is cheaper.",
 keywords:
 "family office cost, how much does a family office cost, single family office cost, family office fees, family office annual budget, cost of running a family office",
 published: "2026-06-02",
 updated: "2026-07-07",
 readTime: 7,
 answer:
 "A dedicated single family office typically costs $1M-$2M a year at the small end (4-6 staff) and $8M-$10M+ for a large office, which works out to roughly 30-120 basis points of assets under management. Setup adds $50,000-$250,000 depending on jurisdiction and structure. Below roughly $50M-$100M in investable assets, a multi-family office, usually charging 0.3%-1% of assets, is almost always the more rational choice.",
 sections: [
 {
 h2: "The honest answer nobody leads with",
 body: [
 "When families ask us what a family office costs, they usually mean the licence fee. That is the smallest number on the page. The licence is thousands of dollars; the office is millions, because a family office is not a registration, it is an operating company with payroll, systems, premises and auditors.",
 "After sixty-plus mandates across the DIFC, Switzerland and Singapore, our rule of thumb has not changed: budget the running cost first, and only then decide whether the structure deserves to exist. The families who get this backwards build offices they quietly dismantle three years later.",
 ],
 },
 {
 h2: "Annual running costs, by office size",
 body: [
 "Industry benchmarks and our own mandates converge on the same brackets. Staff is always the dominant line, typically 60-70% of the total, followed by technology, premises, and professional fees.",
 ],
 table: {
 head: ["Office profile", "Team", "Typical annual cost"],
 rows: [
 ["Lean SFO (outsourced investing)", "2-4 staff", "$700k, $1.5M"],
 ["Standard SFO", "5-8 staff", "$1.5M, $3M"],
 ["Full-capability SFO", "10-15 staff", "$3M, $6M"],
 ["Institutional-grade SFO", "20+ staff", "$8M, $10M+"],
 ],
 },
 },
 {
 h2: "Cost as a percentage of assets",
 body: [
 "Measured against assets under management, family offices cost between roughly 30 and 120 basis points a year. The spread is mostly scale: offices below $500M in AUM average around 100 bps, while offices above $1B fall to 35-40 bps. This is why the size of the balance sheet, not the family's appetite for control, should drive the decision.",
 "A useful test we give every family: if your office's projected annual cost exceeds 1% of investable assets, the structure is consuming the return advantage it was built to protect.",
 ],
 },
 {
 h2: "Setup costs by jurisdiction",
 body: [
 "One-time establishment costs vary more by jurisdiction than families expect. Indicative 2026 figures for a single family office:",
 ],
 table: {
 head: ["Jurisdiction", "Key fees", "Realistic year-one total"],
 rows: [
 ["Dubai (DIFC)", "$8k application + $12k licence + service providers", "$45k, $80k"],
 ["Switzerland", "CHF 5k-15k formation + CHF 100k AG capital", "CHF 60k, 150k"],
 ["Singapore", "Entity + fund structure + advisory", "S$80k, S$200k"],
 ["London (UK)", "Company formation + FCA analysis + advisory", "£40k, £120k"],
 ],
 },
 },
 {
 h2: "When a multi-family office is the cheaper answer",
 body: [
 "A multi-family office typically charges 0.3%-1% of assets, sometimes with fixed retainers. For a family with $30M, that is $90k-$300k a year, against $1M+ to run even a lean dedicated office. The arithmetic only flips somewhere between $50M and $100M+, and even then, families who dislike managing staff often stay with an MFO deliberately.",
 "The decision is rarely purely financial. Control, privacy and the family's operating businesses all weigh in, we cover the full trade-off in our comparison of single and multi-family offices.",
 ],
 },
 ],
 faqs: [
 { q: "What is the minimum wealth needed for a family office?", a: "There is rarely a statutory minimum, but a dedicated single family office usually becomes cost-effective from $50M-$100M in investable assets. Below that, multi-family offices (from as little as $5M-$10M) deliver similar services at a fraction of the cost." },
 { q: "Why do family offices cost so much to run?", a: "Because they are real operating companies: investment and accounting staff, portfolio and reporting technology, premises, custodians, auditors, legal and compliance. Staff alone is typically 60-70% of the budget." },
 { q: "How much does a DIFC family office cost to set up?", a: "In the DIFC, expect an $8,000 application fee, a $12,000 annual commercial licence, plus corporate services and office space, a realistic year-one total of $45,000-$80,000 before staff." },
 { q: "Are family office costs tax-deductible?", a: "It depends on jurisdiction and structure. Some structures allow management-company expenses to be offset; others do not. This is a design decision that should be made at setup, not discovered at the first audit." },
 ],
 sources: [
 { label: "Forge Community, Cost of a Single Family Office Benchmarking Study", url: "https://www.forgecommunity.com/-/media/project/forge/2020-forge-cost-of-running-a-family-office-report.pdf" },
 { label: "J.P. Morgan Private Bank, Starting a family office", url: "https://privatebank.jpmorgan.com/nam/en/insights/markets-and-investing/ideas-and-insights/the-why-who-who-what-and-how-of-starting-a-family-office" },
 { label: "DIFC, Family businesses & Family Arrangements Regulations", url: "https://www.difc.com/business/establish-a-business/family-businesses" },
 ],
 related: [
 { label: "Single vs Multi-Family Office: Which Is Right?", to: "/insights/single-family-office-vs-multi-family-office" },
 { label: "Single Family Office Setup (service)", to: "/single-family-office-setup" },
 { label: "Family Office in Dubai (DIFC), full guide", to: "/family-office-dubai-difc" },
 ],
 },

 "single-family-office-vs-multi-family-office": {
 title: "Single vs Multi-Family Office: Which Is Right for Your Family?",
 category: "Structuring Decisions",
 metaTitle: "Single Family Office vs Multi-Family Office: 2026 Decision Guide | Aurelius",
 metaDescription:
 "SFO vs MFO compared on cost, control, privacy and asset thresholds, with a practical decision framework used with 60+ families. Includes the $50M-$100M crossover rule.",
 keywords:
 "single family office vs multi family office, SFO vs MFO, multi family office minimum, family office comparison, which family office type",
 published: "2026-06-10",
 updated: "2026-07-07",
 readTime: 6,
 answer:
 "A single family office (SFO) serves one family with dedicated staff, maximum control, customisation and privacy, at $1M+ a year. A multi-family office (MFO) shares institutional infrastructure across several families, typically 0.3%-1% of assets, viable from about $5M-$25M. The economic crossover sits around $50M-$100M in investable assets, but control preferences, operating businesses and staff appetite move the line in both directions.",
 sections: [
 {
 h2: "The two models in one table",
 body: [
 "Strip away the marketing and the choice reduces to a handful of variables:",
 ],
 table: {
 head: ["", "Single family office", "Multi-family office"],
 rows: [
 ["Families served", "One, exclusively", "Several, on shared platform"],
 ["Typical fit", "$50M-$100M+ investable", "$5M-$50M (entry from ~$5M)"],
 ["Annual cost", "$1M-$10M+ (fixed)", "0.3%-1% of assets (variable)"],
 ["Control & customisation", "Total", "High, within a framework"],
 ["Privacy", "Maximum, your staff only", "Strong, but shared advisors"],
 ["Time to operational", "2-6 months to build", "Weeks to onboard"],
 ["Licensing", "Often exempt (own family only)", "Regulated (DFSA, FINMA, MAS CMS)"],
 ],
 },
 },
 {
 h2: "When the SFO genuinely wins",
 body: [
 "In our experience, three situations justify the fixed cost of a dedicated office: the family runs significant operating businesses that need coordination alongside the portfolio; the family has complexity that off-the-shelf platforms handle badly (multiple jurisdictions, concentrated positions, aircraft, art, litigation); or confidentiality requirements are absolute, some families will not accept any shared personnel, whatever the saving.",
 "Notice what is not on that list: prestige. An office built as a status symbol is the most expensive business card in finance.",
 ],
 },
 {
 h2: "When the MFO is the smarter structure",
 body: [
 "Below roughly $50M, the MFO is not a compromise, it is the correct engineering. The family gets institutional custody, manager access, consolidated reporting and estate coordination immediately, without recruiting a CIO in a market where good ones cost $500k+ and take a year to find.",
 "The MFO is also the right bridge structure. Several of our families operated within an MFO for five years, then lifted out into a dedicated office once assets, complexity and the next generation justified it, with governance and reporting habits already formed.",
 ],
 },
 {
 h2: "A decision framework that actually settles it",
 body: ["Score your family honestly against five questions:"],
 list: [
 "Assets: are investable assets clearly above $75M? (Below: MFO. Well above: continue.)",
 "Complexity: do you have 3+ jurisdictions, operating companies, or unusual asset classes that platforms serve badly?",
 "People: are you willing to recruit, pay and manage 5-10 senior professionals, including replacing them when they leave?",
 "Privacy: is 'no shared staff, ever' a hard constraint worth seven figures a year?",
 "Horizon: is this structure being built for grandchildren, not for this decade?",
 ],
 },
 ],
 faqs: [
 { q: "What is the minimum for a multi-family office?", a: "Most credible MFOs onboard from $5M-$25M in investable assets, with some premium platforms setting minimums at $50M. This is far below the $50M-$100M+ where a dedicated single family office becomes rational." },
 { q: "Do multi-family offices need a licence?", a: "Yes, serving multiple unrelated families is regulated activity in every major jurisdiction: DFSA authorisation in the DIFC, portfolio-manager licensing under FinIA in Switzerland, and a CMS licence from MAS in Singapore." },
 { q: "Can I switch from an MFO to my own family office later?", a: "Yes, and it is a common path: families build track record, reporting discipline and governance inside an MFO, then lift out into a dedicated SFO once scale justifies it. Structuring the MFO mandate with portability in mind makes the exit clean." },
 { q: "Is a virtual family office a real alternative?", a: "For $20M-$75M families, a 'virtual' office, a lean core (sometimes one professional) orchestrating outsourced specialists, can capture much of the SFO's control at a fraction of the cost. It works only with strong coordination and clear reporting lines." },
 ],
 sources: [
 { label: "J.P. Morgan Private Bank, Multi-family vs single family office", url: "https://privatebank.jpmorgan.com/nam/en/who-we-serve/family-office/single-family-office-vs-multi-family-office" },
 { label: "MAS, Capital Markets Services licensing", url: "https://www.mas.gov.sg/regulation/capital-markets/capital-markets-services-cms-licence" },
 ],
 related: [
 { label: "How Much Does a Family Office Cost?", to: "/insights/how-much-does-a-family-office-cost" },
 { label: "Multi-Family Office Advisory (service)", to: "/multi-family-office-advisory" },
 { label: "Single Family Office Setup (service)", to: "/single-family-office-setup" },
 ],
 },

 "how-to-set-up-a-family-office": {
 title: "How to Set Up a Family Office: The 7-Step Process We Use",
 category: "Setup & Structuring",
 metaTitle: "How to Set Up a Family Office in 2026, 7 Steps, Timeline & Costs | Aurelius",
 metaDescription:
 "The exact 7-step process for setting up a family office: purpose, jurisdiction, structure, licensing, team, systems and governance, with a 2-6 month timeline and real costs.",
 keywords:
 "how to set up a family office, how to start a family office, family office setup process, establishing a family office, family office formation steps",
 published: "2026-06-18",
 updated: "2026-07-07",
 readTime: 8,
 answer:
 "Setting up a family office takes 2-6 months and follows seven steps: (1) define the mandate and services, (2) confirm the economics against your asset base, (3) select the jurisdiction, (4) design the legal structure, (5) obtain licences or exemptions, (6) recruit the team and systems, and (7) codify governance in an investment policy statement and family charter. Most families need $50M+ in investable assets for a dedicated office to make sense.",
 sections: [
 {
 h2: "Before step one: know why the office exists",
 body: [
 "Every failed family office we have been asked to repair skipped the same step: nobody wrote down what the office was for. 'Managing the family's wealth' is not a mandate, it is a slogan. A real mandate says which services are in scope (investments, tax, estates, philanthropy, concierge), who the office serves, and what decisions it may take without the family.",
 "Write the mandate first. Every later decision, jurisdiction, structure, hires, is a function of it.",
 ],
 },
 {
 h2: "The seven steps",
 body: ["This is the sequence we run with families, and the realistic timing for each stage:"],
 list: [
 "1. Define the mandate (weeks 1-2): services in scope, family members served, decision rights, and success measures.",
 "2. Confirm the economics (weeks 2-3): model running costs against assets; below ~$50M-$100M, pivot to a multi-family office.",
 "3. Select the jurisdiction (weeks 3-5): DIFC, Switzerland, Singapore, London or the US, driven by residency, tax, regulation and where the family actually lives.",
 "4. Design the structure (weeks 4-8): holding entities, foundation or trust layer, and the operating company that employs staff.",
 "5. Licensing or exemption (weeks 6-12): most single family offices serving one family are exempt; multi-family activity triggers DFSA / FINMA / MAS licensing.",
 "6. Team and systems (weeks 8-20): the first three hires are usually a head/CIO, a controller, and an operations lead, plus custody, reporting and security stack.",
 "7. Governance go-live (weeks 12-24): investment policy statement signed, family charter adopted, committees seated, first reporting cycle delivered.",
 ],
 },
 {
 h2: "The three mistakes that cost the most",
 body: [
 "First: choosing the jurisdiction for tax alone. A 0% headline rate is worthless if the family's actual life, schools, residency, banking, is eleven hours away. Structure follows life, not the reverse.",
 "Second: hiring the investment team before governance exists. A CIO without an investment policy statement is an unguided missile with a Bloomberg terminal.",
 "Third: building for the founder, not the family. If the structure cannot survive the founder's incapacity, signatures, successors, deadlock rules, it is not a family office, it is a personal office with witnesses.",
 ],
 },
 {
 h2: "What it costs to get to day one",
 body: [
 "Setup costs vary by jurisdiction: roughly $45k-$80k year-one in the DIFC, CHF 60k-150k in Switzerland, S$80k-200k in Singapore, before staff. From there, running costs start around $1M a year for a lean dedicated office. Full breakdown in our family office cost guide.",
 ],
 },
 ],
 faqs: [
 { q: "How long does it take to set up a family office?", a: "Typically 2-6 months end-to-end: faster in the DIFC under the Family Arrangements Regulations (8-12 weeks is common), longer where licensing applies, a Singapore MFO with a CMS licence can take 6-9 months." },
 { q: "Do I need a licence to run my own family office?", a: "Usually not: a genuine single family office serving one family is exempt or outside the regulatory perimeter in the DIFC, Switzerland, Singapore and most major centres. Serving other families changes that immediately." },
 { q: "What should the first three hires be?", a: "A head of office (often doubling as CIO), a financial controller, and an operations/administration lead. Legal, tax and specialist investment expertise are usually better outsourced in year one." },
 { q: "Which jurisdiction is best for a new family office?", a: "There is no universal answer: DIFC leads on tax and speed, Switzerland on discretion and heritage, Singapore on Asian access and incentives, London on legal depth. The right answer follows the family's residency, asset locations and succession plan, we compare them in our jurisdiction guides." },
 ],
 sources: [
 { label: "Deloitte Private, The art of forming your family office", url: "https://www.deloitte.com/us/en/services/deloitte-private/articles/setting-up-family-office.html" },
 { label: "PwC, Building your future-ready family office", url: "https://www.pwc.com/gx/en/services/family-business/family-office/building-your-future-ready-family-office.html" },
 { label: "DIFC Family Arrangements Regulations", url: "https://www.difc.com/business/establish-a-business/family-businesses" },
 ],
 related: [
 { label: "Single Family Office Setup (service)", to: "/single-family-office-setup" },
 { label: "How Much Does a Family Office Cost?", to: "/insights/how-much-does-a-family-office-cost" },
 { label: "Family Office Structure: Roles & Governance", to: "/insights/family-office-structure" },
 ],
 },

 "difc-vs-adgm-family-office": {
 title: "DIFC vs ADGM for Family Offices: An Honest Comparison",
 category: "Jurisdictions",
 metaTitle: "DIFC vs ADGM Family Office (2026): Fees, Regulation & Which to Choose | Aurelius",
 metaDescription:
 "DIFC or ADGM for your family office? Fees, Family Arrangements Regulations vs ADGM's regime, banking depth, substance and privacy, compared by advisors who file in both.",
 keywords:
 "DIFC vs ADGM family office, ADGM family office, DIFC family office, Abu Dhabi vs Dubai family office, UAE family office comparison",
 published: "2026-06-25",
 updated: "2026-07-07",
 readTime: 6,
 answer:
 "Both DIFC (Dubai) and ADGM (Abu Dhabi) offer 0% personal income tax, English common-law courts and credible family-office regimes. DIFC has the deeper private-banking ecosystem, more family offices in residence and the streamlined Family Arrangements Regulations; ADGM counters with lower fees, strong sovereign-wealth adjacency and a fast-improving regime. Families anchored to Dubai's lifestyle and banking choose DIFC; cost-sensitive families or those with Abu Dhabi business ties choose ADGM.",
 sections: [
 {
 h2: "What they share",
 body: [
 "Start with the common ground, because it is substantial: both centres operate independent English-language common-law courts, both sit inside the UAE's 0% personal income tax environment, both offer 100% foreign ownership, and both have deliberately courted family capital with dedicated regimes. A family choosing between them is choosing between two good answers.",
 ],
 },
 {
 h2: "Where they genuinely differ",
 body: ["The differences that actually move decisions in our mandates:"],
 table: {
 head: ["Factor", "DIFC (Dubai)", "ADGM (Abu Dhabi)"],
 rows: [
 ["Family-office regime", "Family Arrangements Regulations, qualifying SFOs need no DFSA licence", "SPV / Foundations regime + family-office framework"],
 ["Ecosystem", "120+ family offices; deepest private-banking cluster in the region", "Growing; strong sovereign and institutional adjacency"],
 ["Indicative costs", "Higher, $8k application, $12k licence, pricier space", "Generally lower fees and office costs"],
 ["Courts", "DIFC Courts (est. 2004, deep case law)", "ADGM Courts (est. 2016, direct English-law application)"],
 ["Lifestyle anchor", "Dubai, schools, flights, family infrastructure", "Abu Dhabi, quieter, government-linked economy"],
 ],
 },
 },
 {
 h2: "How we actually advise families to choose",
 body: [
 "Ask where the family will physically live and bank. If the answer is Dubai, as it is for most international families relocating to the UAE, DIFC's ecosystem advantage compounds: bankers, lawyers, other family offices and deal flow are a lift ride away, not a 90-minute drive.",
 "ADGM wins on arithmetic and on Abu Dhabi anchoring: materially lower running costs, and genuine advantages for families with sovereign-linked business or Abu Dhabi operating companies. Its foundations regime is also excellent for holding structures even when the operating office sits elsewhere, a hybrid we use often.",
 "The honest summary: DIFC is the default for lifestyle-led relocations; ADGM is the value play and the foundations toolbox. Many sophisticated structures use both.",
 ],
 },
 ],
 faqs: [
 { q: "Is ADGM cheaper than DIFC for a family office?", a: "Generally yes, ADGM's registration and licence fees and office costs run lower than DIFC's ($8,000 application plus $12,000 annual licence, plus premium rents). For lean structures the difference is meaningful; for large offices it is rounding." },
 { q: "Do I need a regulator's licence for a single family office in either centre?", a: "For a qualifying single family office serving one family: no in the DIFC under the Family Arrangements Regulations, and ADGM offers comparable exemption paths. Multi-family activity is regulated in both (DFSA / FSRA)." },
 { q: "Can I combine ADGM and DIFC structures?", a: "Yes, and it is common: for example an ADGM foundation as the holding/succession layer with a DIFC entity as the operating family office. The two centres' regimes are complementary rather than mutually exclusive." },
 { q: "Which has better asset-protection foundations?", a: "Both offer strong common-law foundations regimes. ADGM's is particularly well regarded for flexibility and cost; DIFC's benefits from the longer-established court system. The right choice depends on where control, assets and disputes are likely to sit." },
 ],
 sources: [
 { label: "DIFC, Family businesses", url: "https://www.difc.com/business/establish-a-business/family-businesses" },
 { label: "ADGM, Foundations & SPV regimes", url: "https://www.adgm.com/setting-up" },
 ],
 related: [
 { label: "Family Office in Dubai (DIFC), full guide", to: "/family-office-dubai-difc" },
 { label: "Wealth Structuring (service)", to: "/wealth-structuring" },
 { label: "Where Wealthy Families Are Moving in 2026", to: "/insights/where-wealthy-families-are-moving" },
 ],
 },

 "family-office-structure": {
 title: "Family Office Structure: Roles, Org Chart and Governance That Work",
 category: "Governance",
 metaTitle: "Family Office Structure 2026: Org Chart, Roles & Governance | Aurelius",
 metaDescription:
 "How a family office is structured: legal layers, the org chart from CIO to controller, which roles to hire vs outsource, and the governance documents that keep it working.",
 keywords:
 "family office structure, family office org chart, family office roles, family office governance, family office team, family office organizational structure",
 published: "2026-07-01",
 updated: "2026-07-07",
 readTime: 7,
 answer:
 "A family office has three layers: a legal layer (holding entities, trust or foundation, and an operating company that employs staff), a people layer (typically a head/CIO, controller, operations lead, then specialists), and a governance layer (family charter, investment policy statement, and committees). Small offices run with 2-6 people and outsource the rest; the design question is never 'what can we build' but 'what must be in-house'.",
 sections: [
 {
 h2: "The three layers every office has",
 body: [
 "Families often conflate the legal structure with the office. Keep three layers distinct: the legal layer holds and protects assets (holding companies, trusts or foundations, SPVs); the operating layer is the management company that employs people and signs vendor contracts; the governance layer is the paper that connects the office to the family, charter, investment policy, committee mandates.",
 "When something goes wrong in a family office, the failure is almost always in the seams between layers: an operating company acting without a mandate, or a trust whose trustees were never told what the family actually wants.",
 ],
 },
 {
 h2: "The org chart, from lean to institutional",
 body: [
 "There is no single correct chart, but there are recurring shapes. What matters is which functions are owned in-house versus orchestrated:",
 ],
 table: {
 head: ["Function", "Lean office (2-6 people)", "Institutional office (15+)"],
 rows: [
 ["Leadership", "Head of family office (often CIO)", "CEO + CIO, separate"],
 ["Investments", "CIO + outsourced managers", "In-house analysts, asset-class teams"],
 ["Finance & reporting", "Controller", "CFO, fund accountants"],
 ["Legal & tax", "Outsourced", "General counsel + external"],
 ["Operations & lifestyle", "Ops lead handles both", "Dedicated concierge/estate staff"],
 ["Risk & compliance", "Outsourced compliance officer", "In-house CCO + cyber lead"],
 ],
 },
 },
 {
 h2: "The governance paper that earns its keep",
 body: ["Three documents do most of the work in a healthy office:"],
 list: [
 "Family charter, the family's values, who is served, how decisions are made, and how disputes are resolved. Morally binding, and read more often than any legal document.",
 "Investment policy statement (IPS), objectives, asset allocation ranges, liquidity rules, restrictions, and who may approve exceptions. The CIO's contract with the family.",
 "Committee mandates, what the investment committee (and any board) may decide, its membership, quorum and reporting. Separates ownership from management before a crisis does.",
 ],
 },
 {
 h2: "In-house or outsourced: the only design question",
 body: [
 "Our default for new offices: keep control functions in-house (leadership, reporting, decision rights) and buy expertise (tax, legal, specialist investing, cybersecurity) until scale forces internalisation. An office that hires specialists before it has reporting discipline gets expensive opinions and no accountability.",
 ],
 },
 ],
 faqs: [
 { q: "What roles does a family office need first?", a: "The proven first three: a head of office (often doubling as CIO), a financial controller for consolidated reporting, and an operations lead. Everything else, tax, legal, specialist mandates, is better outsourced initially." },
 { q: "What is the difference between a family office CEO and CIO?", a: "The CEO runs the office as a business, staff, vendors, governance, family relationships. The CIO runs the capital, allocation, manager selection, risk. In offices under ~8 people one person usually wears both hats; above that, splitting them is best practice." },
 { q: "Should the family office own the family's assets?", a: "Usually not. Assets typically sit in holding entities, trusts or foundations; the family office is a management company that serves them. Mixing custody of assets with operations concentrates risk and muddies liability." },
 { q: "How many people work in a typical family office?", a: "Most single family offices worldwide run with fewer than 10 staff; 2-6 is the most common band, with functions like tax, legal and cybersecurity outsourced. Institutional offices serving $1B+ commonly employ 15-30." },
 ],
 sources: [
 { label: "Citi Private Bank, A guide to establishing a family office", url: "https://www.privatebank.citibank.com/ivc/docs/A-guide-to-establishing-a-family-office.pdf" },
 { label: "BNY Wealth, Creating a successful family office", url: "https://www.bny.com/wealth/global/en/insights/key-considerations-when-creating-a-family-office.html" },
 ],
 related: [
 { label: "Family Governance (service)", to: "/family-governance" },
 { label: "How to Set Up a Family Office", to: "/insights/how-to-set-up-a-family-office" },
 { label: "Succession Planning (service)", to: "/succession-planning" },
 ],
 },

 "where-wealthy-families-are-moving": {
 title: "Where Wealthy Families Are Moving in 2026, and Why",
 category: "Wealth Migration",
 metaTitle: "Where Are the Wealthy Moving in 2026? UAE, Switzerland, Italy & Singapore | Aurelius",
 metaDescription:
 "After the UK scrapped non-dom status, record numbers of HNW families are relocating. Where they are going in 2026, UAE, Switzerland, Italy, Singapore, and how each regime works.",
 keywords:
 "where are wealthy moving, millionaire migration 2026, UK non dom leaving, wealthy leaving UK, best country for wealthy families, HNWI relocation",
 published: "2026-07-05",
 updated: "2026-07-07",
 readTime: 7,
 answer:
 "Since the UK abolished the non-dom regime in April 2025, wealthy families have been relocating in record numbers. The four destinations dominating 2026: the UAE (0% personal income tax, DIFC/ADGM family-office regimes, golden visas), Switzerland (lump-sum taxation and stability), Italy (flat tax of €200,000 a year on foreign income), and Singapore (Asia's hub with strong fund incentives). The winners share three traits: predictable tax treatment, credible legal systems and genuine lifestyle infrastructure.",
 sections: [
 {
 h2: "The trigger: Britain ends two centuries of non-dom",
 body: [
 "In April 2025 the UK abolished non-domiciled tax status, ending a regime that had made London the default base for international wealth since the Napoleonic wars. The replacement, a four-year residence-based scheme, is short, and inheritance-tax exposure on worldwide assets is the detail that moves families: it is one thing to pay tax on income, another to expose a century of family capital to 40% on death.",
 "Advisory desks including ours saw the consequence immediately: the question changed from 'should we leave?' to 'which of these four, and how fast?'",
 ],
 },
 {
 h2: "The four destinations winning 2026",
 body: ["Each of the leading destinations solves a different equation:"],
 table: {
 head: ["Destination", "The tax proposition", "Best suited to"],
 rows: [
 ["UAE (Dubai / Abu Dhabi)", "0% personal income & capital gains; golden visas; DIFC/ADGM regimes", "Families wanting zero tax with real infrastructure and Gulf/Asia reach"],
 ["Switzerland", "Lump-sum (forfait) taxation negotiated by canton; no need to disclose worldwide income", "Privacy-led families anchoring in Europe long-term"],
 ["Italy", "€200,000 flat tax per year on all foreign income (15-year cap)", "Families wanting EU lifestyle with fixed, predictable cost"],
 ["Singapore", "Territorial-leaning taxation; 13O/13U fund incentives; no capital gains tax", "Asia-facing families and fund-heavy balance sheets"],
 ],
 },
 },
 {
 h2: "What the migration data shows",
 body: [
 "Industry migration trackers have projected record millionaire flows through 2025-2026, with the UAE the single largest net recipient and the UK among the largest net losers. Dubai's family-office population has grown to well over a hundred offices in the DIFC alone, and Milan, Zug, Geneva and Singapore have all reported waiting lists in the schools that relocating families actually check first.",
 "The pattern beneath the headlines: families are not chasing the lowest rate, they are buying certainty. Regimes that changed retroactively (or might) lose; regimes with published rules and decade-long horizons win.",
 ],
 },
 {
 h2: "Relocation is a structuring event, not a flight",
 body: [
 "Changing residence without restructuring is the most expensive mistake in wealth migration. Exit taxes, trailing inheritance-tax exposure, treaty gaps and badly-timed disposals can consume years of the expected saving. The sequence matters: structure first (holding entities, trusts or foundations aligned to the destination), then move, then unwind what the old residence required.",
 "Our redomiciliation work typically pairs a destination decision with a full structural review, the same exercise behind our jurisdiction guides for the DIFC, Switzerland and Singapore.",
 ],
 },
 ],
 faqs: [
 { q: "Why are wealthy families leaving the UK?", a: "The abolition of non-dom status in April 2025 exposed long-term international residents to UK tax, most critically inheritance tax on worldwide assets, removing the regime that had made London the preferred base for global wealth." },
 { q: "Which country has no income tax for wealthy residents?", a: "The UAE levies no personal income tax, no capital gains tax and no inheritance tax on individuals, which, combined with golden visas and the DIFC/ADGM family-office regimes, has made it the top destination for migrating wealth." },
 { q: "How does Italy's flat tax for the wealthy work?", a: "New residents can elect a flat tax of €200,000 per year covering all foreign-source income (doubled from €100,000 in 2024), for up to 15 years, with family members addable for €25,000 each. Italian-source income is taxed normally." },
 { q: "Is Switzerland's lump-sum taxation still available?", a: "Yes, in most cantons: qualifying foreign nationals who do not work in Switzerland can negotiate expenditure-based taxation, decoupling their Swiss tax bill from worldwide income and assets. Minimum taxable bases vary by canton." },
 ],
 sources: [
 { label: "UK Government, Abolition of the non-dom regime (policy paper)", url: "https://www.gov.uk/government/publications/changes-to-the-taxation-of-non-uk-domiciled-individuals" },
 { label: "Henley & Partners, Private Wealth Migration Report", url: "https://www.henleyglobal.com/publications" },
 { label: "Agenzia delle Entrate, New residents flat-tax regime", url: "https://www.agenziaentrate.gov.it/portale/web/english" },
 ],
 related: [
 { label: "Family Office in Dubai (DIFC), full guide", to: "/family-office-dubai-difc" },
 { label: "Family Office in Switzerland, full guide", to: "/family-office-switzerland" },
 { label: "DIFC vs ADGM for Family Offices", to: "/insights/difc-vs-adgm-family-office" },
 ],
 },
};

export const blogOrder = [
 "where-wealthy-families-are-moving",
 "how-much-does-a-family-office-cost",
 "single-family-office-vs-multi-family-office",
 "how-to-set-up-a-family-office",
 "difc-vs-adgm-family-office",
 "family-office-structure",
];

// Hero/card image per post (files under public/img).
export const blogImages = {
  "how-much-does-a-family-office-cost": "/img/blog-cost.jpg",
  "single-family-office-vs-multi-family-office": "/img/blog-sfomfo.jpg",
  "how-to-set-up-a-family-office": "/img/blog-setup.jpg",
  "difc-vs-adgm-family-office": "/img/blog-difcadgm.jpg",
  "family-office-structure": "/img/blog-structure.jpg",
  "where-wealthy-families-are-moving": "/img/blog-migration.jpg",
};
